import React from 'react'; // eslint-disable-line no-unused-vars
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import { convertToCurrency } from '@utilities/tools';

export interface ImpactDonutProps {
  budget: number;
  defundedAmount: number;
}

const CenterText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontWeight: 600,
}));

const fmtCompactUSD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 2,
});

function PieCenterLabel({
  percentCutText,
  amountLostText,
}: {
  percentCutText: string;
  amountLostText: string;
}) {
  const { width, height, left, top } = useDrawingArea();
  const cx = left + width / 2;
  const cy = top + height / 2;

  return (
    <CenterText x={cx} y={cy}>
      <tspan x={cx} y={cy} style={{ fontSize: 20 }}>
        {percentCutText} Needed
      </tspan>
      <tspan x={cx} dy="1.2em" style={{ fontSize: 20, fontWeight: 500, opacity: 0.8 }}>
        {amountLostText}
      </tspan>
    </CenterText>
  );
}

export const ImpactDonut = ({
  budget,
  defundedAmount,
}: ImpactDonutProps): React.JSX.Element | null => {
  if (budget && budget > 0 && defundedAmount && defundedAmount > 0 && budget >= defundedAmount) {
    const rawPct = budget > 0 ? Math.round((defundedAmount / budget) * 100) : 0;
    const percentCutText = rawPct >= 1 ? `${Math.round(rawPct)}%` : `${rawPct.toFixed(1)}%`;

    const LARGE_TOTAL_THRESHOLD = 1_000_000;
    const useCompact = budget >= LARGE_TOTAL_THRESHOLD;

    const compactOrFull = (n: number) =>
      useCompact ? fmtCompactUSD.format(n) : convertToCurrency(n);

    const valueFormatter = (item: { value: number }) => compactOrFull(item.value);

    return (
      <PieChart
        width={420}
        height={320}
        hideLegend={true}
        series={[
          {
            innerRadius: 80,
            outerRadius: 130,
            data: [
              {
                id: 0,
                color: '#2CAEC9',
                value: budget - defundedAmount,
                label: 'Budget',
              },
              {
                id: 1,
                color: '#E65222',
                value: defundedAmount,
                label: 'Defunded',
              },
            ],
            valueFormatter,
          },
        ]}
      >
        <PieCenterLabel
          percentCutText={`${percentCutText}`}
          amountLostText={compactOrFull(defundedAmount)}
        />
      </PieChart>
    );
  } else {
    return null;
  }
};
